/* eslint-disable import/prefer-default-export */
import { createTheme } from '@mui/material/styles';
import { formatHex, parse } from 'culori';
import twColors from 'tailwindcss/colors';

import {
  muiButtonLinkRootOverride,
  muiButtonLinkStyleOverrides,
} from './muiButtonLinkStyles';
import {
  muiOverlayComponentsV5,
  muiPaperElevation8DataGridPanel,
} from './muiOverlaySurfaces';
import { muiHeaderTypography } from './muiTypography';
import {
  getMuiTabsButtonedCompactStyles,
  getMuiTabsButtonedStyles,
} from './muiTabsButtoned';

function twHex(color) {
  if (typeof color !== 'string') return color;
  if (!color.startsWith('oklch(')) return color;

  const parsed = parse(color);
  if (!parsed) return color;

  // `formatHex` returns `#rrggbb` when alpha is 1
  return formatHex(parsed);
}

export const muiThemeV5 = createTheme({
  shape: {
    // Matches Tailwind `rounded-xl` (0.75rem) with `html` font-size 14px from CssBaseline below.
    borderRadius: 10.5,
  },
  palette: {
    mode: 'light',
    primary: {
      main: twHex(twColors.blue[600]),
    },
    secondary: {
      main: twHex(twColors.blue[600]),
    },
    action: {
      active: 'rgba(0, 0, 0, 0.6)',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
  },
  typography: {
    // Make 1rem == 14px (and keep pxToRem consistent).
    htmlFontSize: 14,
    fontSize: 14,
    fontFamily:
      "'Inter', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif",
    ...muiHeaderTypography,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontSize: 14,
        },
        body: {
          fontSize: '1rem',
        },
        'table.MuiTable-root:has(> tbody.MuiTableBody-root:empty) thead.MuiTableHead-root th.MuiTableCell-head':
          {
            borderBottom: 0,
            borderBottomWidth: 0,
          },
        ':has(.MuiTablePagination-root) table.MuiTable-root:has(> tbody.MuiTableBody-root:empty) thead.MuiTableHead-root th.MuiTableCell-head':
          {
            borderBottom: '1px solid var(--tw-color-gray-200)',
            borderBottomWidth: 1,
          },
        'table.MuiTable-root:not(:has(> tfoot.MuiTableFooter-root)) > tbody.MuiTableBody-root > tr.MuiTableRow-root:last-of-type > :is(td.MuiTableCell-root, th.MuiTableCell-root)':
          {
            borderBottom: 0,
            borderBottomWidth: 0,
          },
        'table.MuiTable-root > tfoot.MuiTableFooter-root > tr.MuiTableRow-root > :is(td.MuiTableCell-root, th.MuiTableCell-root)':
          {
            borderTop: '1px solid var(--tw-color-gray-200)',
          },
      },
    },
    MUIDataTable: {
      styleOverrides: {
        outlined: {
          border: '1px solid var(--tw-color-gray-200)',
        },
        paper: {
          boxShadow: 'none',
          '&:not(.MuiPaper-elevation0)': {
            border: '1px solid var(--tw-color-gray-200)',
          },
          '.MuiCard-root &': {
            border: 0,
            borderWidth: 0,
          },
          '.MuiCard-root &[class*="MuiPaper-elevation"]': {
            border: 0,
            borderWidth: 0,
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 'var(--tw-radius-xl)',
          overflow: 'hidden',
          '&:first-of-type': {
            borderTopLeftRadius: 'var(--tw-radius-xl)',
            borderTopRightRadius: 'var(--tw-radius-xl)',
          },
          '&:last-of-type': {
            borderBottomLeftRadius: 'var(--tw-radius-xl)',
            borderBottomRightRadius: 'var(--tw-radius-xl)',
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '.MuiAccordion-root &': {
            border: 0,
            borderWidth: 0,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          justifyContent: 'space-between',
          padding: '0.5rem 1.5rem 1.25rem 1.5rem',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        // Prevent double-line effect when v4 + v5 Divider styles collide.
        root: ({ ownerState, theme }) => {
          const common = {
            border: 0,
            backgroundColor: theme.palette.divider,
          };

          if (ownerState?.orientation === 'vertical') {
            return {
              ...common,
              width: 1,
              height: '100%',
            };
          }

          return {
            ...common,
            height: 1,
          };
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          ...muiButtonLinkRootOverride,
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
          '&.Mui-focusVisible': {
            boxShadow: 'none',
          },
        },
        ...muiButtonLinkStyleOverrides,
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          borderBottomWidth: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation0: {
          boxShadow: 'none',
        },
        elevation1: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation2: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation3: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation4: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation5: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation6: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation7: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation8: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
          ...muiPaperElevation8DataGridPanel,
        },
        elevation9: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation10: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation11: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation12: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation13: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation14: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation15: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation16: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation17: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation18: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation19: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation20: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation21: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation22: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation23: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
        elevation24: {
          boxShadow: 'none',
          border: '1px solid var(--tw-color-gray-200)',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: 'rgba(0, 0, 0, 0.6)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
        }),
        bar: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
        }),
      },
    },
    MuiTabs: {
      variants: [
        {
          props: { variant: 'buttoned' },
          style: ({ theme }) => getMuiTabsButtonedStyles(theme),
        },
        {
          props: { variant: 'buttonedCompact' },
          style: ({ theme }) => getMuiTabsButtonedCompactStyles(theme),
        },
      ],
      styleOverrides: {
        indicator: {
          backgroundColor: twHex(twColors.blue[600]),
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '&.Mui-selected': {
            color: twHex(twColors.blue[600]),
          },
        },
      },
    },
    ...muiOverlayComponentsV5,
  },
});
